import { languageCrudConfig, type Language } from '@/config/objects/languages';
import {
  CrudDetailView,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

export function LanguageDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (languageId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/languages/${languageId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['Language'] });
        navigate('/languages');
      } else {
        throw new Error(response.error || 'Failed to delete language');
      }
    },
  });

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Language Not Found</h2>
          <p className="text-muted-foreground">The requested language could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  const handleEdit = (language: Language) => {
    navigate(`/languages/${language.id}/edit`);
  };

  const handleDelete = async (language: Language) => {
    return toast.promise(deleteMutation.mutateAsync(language.id), {
      loading: 'Deleting language...',
      success: 'Language deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete language',
    });
  };

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Languages', href: '/languages' },
    { label: 'Details', current: true },
  ];

  return (
    <PageContainer>
      <CrudDetailView
        config={languageCrudConfig}
        itemId={id}
        breadcrumbs={breadcrumbs}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    </PageContainer>
  );
}
